import { uniqBy } from 'lodash';
import { baseApi } from 'core/api';

const state = () => ({
    _items: null,
    _more: null,
    _total: 0,
    _search: {
        items: null,
        more: null,
        total: 0,
        term: null,
    },
    _isAllowed: false,
    _formData: null,
});

const getters = {
    contactImports: (state) => {
        if (state._search.term !== null) {
            return state._search.items;
        } else {
            return state._items;
        }
    },
    contactImportsTotal: (state) => {
        if (state._search.term !== null) {
            return state._search.total;
        } else {
            return state._total;
        }
    },
    contactImportsHaveMore: (state) => {
        if (state._search.term !== null) {
            return state._search.more !== null;
        } else {
            return state._more !== null;
        }
    },
    contactImportsSearchTerm: (state) => state._search.term,

    firstContactsImportIsAllowed: (state) => state._isAllowed,

    contactsImportFormData: (state) => state._formData,
};

const actions = {
    /**
     * Loads the contacts from the backend.
     *
     * @param {Object} config
     * @return {Promise}
     */
    contactImportsLoad(config) {
        let url = 'core/contacts/import';

        if (!config) {
            config = {
                retry: false,
                update: false,
            };
        }

        if (!config.update) {
            if (!config.retry && this._more) {
                url = this._more;
            } else if (config.retry === true) {
                this._setContactImportsRetryLoading();
            }
        }

        return baseApi.get(url).then((response) => {
            this._setContactImportsLoaded({
                data: response.data,
                update: config.update,
            });

            return response;
        });
    },

    /**
     * Clears the search results.
     *
     * @return {void}
     */
    contactImportsClearSearch() {
        this._setContactImportsSearchCleared();
    },

    /**
     * Adds the given contact to the stack.
     *
     * @param {Object} contact
     * @return {void}
     */
    contactImportCreated(contact) {
        this._setContactImportCreated(contact);
    },

    contactFormData(data) {
        this._setContactImportFormData(data);
    },

    /**
     * Triggered when a contact is updated.
     *
     * @param {Object} contact
     * @return {void}
     */
    contactImportUpdated(contact) {
        this._setContactImportUpdated(contact);
    },

    contactImportDelete({ id, form }) {
        return form.delete(`core/contacts/import/${id}`).then((response) => {
            this._setContactImportDeleted({
                id: id,
            });

            this.contactImportsLoad();
            return response;
        });
    },

    /**
     * Removes the given contact ID from the list.
     *
     * @param {Number} id
     * @return {void}
     */
    contactImportDeleted({ id }) {
        this._setContactImportDeleted({
            id: id,
        });
    },

    /**
     * Removes the given contact IDs from the list.
     *
     * @param {Number} ids
     * @return {void}
     */
    contactImportsBulkDeleted(ids) {
        this._setContactImportsBulkDeleted(ids);
    },

    contactImportsClear() {
        this._setContactImportsCleared();
    },

    updateStatusFirstContactsImport(status) {
        this._setUpdateStatusFirstContactsImport(status);
    },

    getContactsImportProgressById({ id }) {
        return baseApi.get(`core/contacts/import/progress/${id}`).then((response) => {
            return response.data;
        });
    },

    updateContactsImportList({ data = {}, options = {} }) {
        return baseApi.post('core/contacts/import/list', data, options).then((response) => {
            return response.data;
        });
    },

    updateContactsImportUpload({ data = {}, options = {} }) {
        return baseApi.post('core/contacts/import/upload', data, options).then((response) => {
            return response.data;
        });
    },

    updateContactsImportStartById({ form, id }) {
        return form.post(`core/contacts/import/start/${id}`).then((response) => {
            return response.data;
        });
    },

    getContactsImportStatus() {
        return baseApi.get('core/contacts/import/status').then((response) => {
            return response.data;
        });
    },

    updateContactsImportRequestReviewById({ id, params = {} }) {
        return baseApi
            .post(`core/contacts/import/request/review/${id}`, params)
            .then((response) => {
                return response.data;
            });
    },

    // Private methods for all operations
    _setContactImportCreated(data) {
        if (this._items === null) {
            return;
        }

        const newItems = this._items.map((item) => {
            if (item.id === data.id) {
                return data;
            } else {
                return item;
            }
        });

        this._items = newItems;
        ++this._total;
    },

    _setContactImportsCleared() {
        this._items = [];
    },

    _setUpdateStatusFirstContactsImport(status) {
        this._isAllowed = status;
    },

    _setContactImportUpdated(data) {
        if (this._items === null) {
            return;
        }

        this._items = this._items.map((o) => {
            if (o.id === data.id) {
                return data;
            } else {
                return o;
            }
        });

        if (this._search.results) {
            this._search.results = this._search.results.map((o) => {
                if (o.id === data.id) {
                    return data;
                } else {
                    return o;
                }
            });
        }
    },

    _setContactImportDeleted({ id }) {
        if (this._items === null) {
            return;
        }

        this._items = this._items.filter((o) => o.id !== id);
        --this._total;

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => o.id !== id);
        }
    },

    _setContactImportsBulkDeleted(ids) {
        if (!this._items) return;

        this._items = this._items.filter((o) => {
            return ids.indexOf(o.id) === -1;
        });

        this._total = this._total - ids.length;

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => {
                return ids.indexOf(o.id) === -1;
            });
        }
    },

    _setContactImportsLoaded(config) {
        if (!config.update && this._more) {
            for (let i in config.data.data) {
                this._items.push(config.data.data[i]);
            }
        } else {
            this._items = config.data.data;
        }

        this._more = config.data.next_page_url;
        this._total = config.data.total;
    },

    _setContactImportsRetryLoading() {
        this._items = null;
        this._more = null;
        this._total = 0;
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null,
        };
    },

    _setContactImportsErrorLoading() {
        this._items = false;
        this._more = null;
        this._total = 0;
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null,
        };
    },

    _setContactImportsSearchLoaded({ term, data }) {
        if (this._search.items === null) {
            this._search.items = data.data;
        } else {
            for (let i in data.data) {
                this._search.items.push(data.data[i]);
            }

            this._search.items = uniqBy(this._search.items, 'id');
        }

        this._search.more = data.next_page_url;
        this._search.total = data.total;
        this._search.term = term;
    },

    _setContactImportsSearchError() {
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null,
        };
    },

    _setContactImportsSearchCleared() {
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null,
        };
    },

    _setContactImportFormData(data) {
        this._formData = data;
    }
};

export default {
    state,
    getters,
    actions
};
